import {
	type ChangeEventHandler,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { cn } from 'utils/classNames';

interface Props {
	className?: string;
	disabled?: boolean;
	id: string;
	label: string;
	max: number;
	min: number;
	onChange: ChangeEventHandler<HTMLInputElement>;
	step?: number;
	steps?: (string | number)[];
	value?: number;
}

export default function Range({
	className,
	disabled,
	id,
	label,
	max,
	min,
	onChange,
	step,
	steps,
	value,
}: Props) {
	const getProgress = useCallback(
		(val: number | undefined) =>
			val === undefined || Number.isNaN(val)
				? 0
				: Math.round(((val - min) / (max - min)) * 100),
		[min, max],
	);

	const [progress, setProgress] = useState<number>(getProgress(value));

	// Keep progress in sync with controlled value and min/max changes.
	const inputRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		setProgress(getProgress(Number(inputRef.current?.value)));
	}, [value, min, max, getProgress]);

	return (
		<div className={cn(className, disabled && 'opacity-50')}>
			<label className="mb-2 inline-block" htmlFor={id}>
				{label}
			</label>
			<input
				ref={inputRef}
				id={id}
				type="range"
				value={value}
				min={min}
				max={max}
				step={step}
				disabled={disabled}
				onChange={(e) => {
					// Keep progress in sync with uncontrolled value.
					if (value === undefined) {
						setProgress(getProgress(Number(e.target.value)));
					}
					onChange(e);
				}}
				style={{ '--progress': `${progress}%` }}
				className={cn(
					'h-6 w-full appearance-none bg-transparent',

					// Chrome/Safari
					'[&::-webkit-slider-runnable-track]:h-2',
					'[&::-webkit-slider-runnable-track]:border-0',
					'[&::-webkit-slider-runnable-track]:rounded-full',
					'[&::-webkit-slider-runnable-track]:bg-brand',
					'[&::-webkit-slider-runnable-track]:bg-[linear-gradient(to_right,var(--background-color-brand)_0%,var(--background-color-brand)_var(--progress),var(--background-color-subtle)_var(--progress),var(--background-color-subtle)_100%)]',
					'[&::-webkit-slider-thumb]:appearance-none',
					'[&::-webkit-slider-thumb]:size-6',
					'[&::-webkit-slider-thumb]:-mt-2',
					'[&::-webkit-slider-thumb]:border-0',
					'[&::-webkit-slider-thumb]:rounded-full',
					'[&::-webkit-slider-thumb]:bg-brand',

					// Firefox
					'[&::-moz-range-track]:w-full',
					'[&::-moz-range-track]:h-2',
					'[&::-moz-range-track]:border-0',
					'[&::-moz-range-track]:rounded-full',
					'[&::-moz-range-track]:bg-subtle',
					'[&::-moz-range-progress]:h-2',
					'[&::-moz-range-progress]:rounded-full',
					'[&::-moz-range-progress]:bg-brand',
					'[&::-moz-range-thumb]:bg-brand',
					'[&::-moz-range-thumb]:size-6',
					'[&::-moz-range-thumb]:border-0',
					'[&::-moz-range-thumb]:rounded-full',

					// Forced colors mode
					'forced-color-adjust-none',
					'forced-colors:[&::-webkit-slider-runnable-track]:bg-[CanvasText]',
					'forced-colors:[&::-webkit-slider-runnable-track]:outline-solid',
					'forced-colors:[&::-webkit-slider-runnable-track]:outline-1',
					'forced-colors:[&::-webkit-slider-runnable-track]:bg-[linear-gradient(to_right,CanvasText_0%,CanvasText_var(--progress),Field_var(--progress),Field_100%)]',
					'forced-colors:[&::-webkit-slider-thumb]:bg-[CanvasText]',
					'forced-colors:[&::-moz-range-progress]:bg-[CanvasText]',
					'forced-colors:[&::-moz-range-thumb]:bg-[CanvasText]',
					'forced-colors:[&::-moz-range-track]:bg-[Field]',
					'forced-colors:[&::-moz-range-track]:outline-solid',
					'forced-colors:[&::-moz-range-track]:outline-1',
				)}
			/>
			{steps && (
				/* Set aria-hidden since the labels' context is all visual. */
				<p className="flex justify-between px-1" aria-hidden="true">
					{steps.map((stepLabel) => (
						<span key={stepLabel} className="min-w-[1.5em] text-center text-sm">
							{stepLabel}
						</span>
					))}
				</p>
			)}
		</div>
	);
}
Range.displayName = 'FormUI_Range';
